import { ArrowUpRight } from "lucide-react";
import { PROJECTS } from "../../data/content";
import { Reveal, Eyebrow } from "./Reveal";

const CornerTicks = () => (
  <>
    <span className="absolute left-3 top-3 h-3 w-3 border-l border-t border-[#9c7a33]/60" />
    <span className="absolute right-3 top-3 h-3 w-3 border-r border-t border-[#9c7a33]/60" />
    <span className="absolute bottom-3 left-3 h-3 w-3 border-b border-l border-[#9c7a33]/60" />
    <span className="absolute bottom-3 right-3 h-3 w-3 border-b border-r border-[#9c7a33]/60" />
  </>
);

export default function Technology() {
  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="border-b border-[var(--line)] bg-[#f4f2ec]"
    >
      <div className="mx-auto max-w-[1400px] px-6 py-28 md:px-10 md:py-40">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <Reveal>
              <Eyebrow testid="projects-eyebrow">{PROJECTS.eyebrow}</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-7 font-display text-4xl font-light tracking-tight text-[#1c2b4a] md:text-5xl lg:text-6xl">
                {PROJECTS.heading}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#5c6474]">
              Lab Index — 08 Technologies
            </span>
          </Reveal>
        </div>

        {/* Featured device work — specimen plates */}
        <div className="mt-16 grid gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-5" delay={0.05}>
            <figure
              data-testid="tech-feature-demo"
              className="group relative h-[440px] overflow-hidden rounded-sm border border-[var(--line)] md:h-full md:min-h-[560px]"
            >
              <img
                src="/tech-demo.jpg"
                alt="Adonica Shaw prototyping a medical device by hand"
                className="h-full w-full object-cover object-[center_22%] transition-transform duration-700 group-hover:scale-105"
              />
              <span className="absolute left-4 top-4 rounded-sm bg-[#1c2b4a]/85 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#faf8f5] backdrop-blur">
                [ Fig. 01 — Prototype Trials ]
              </span>
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1c2b4a]/85 via-[#1c2b4a]/30 to-transparent p-7">
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#cbb890]">
                  Designed for precision
                </span>
                <p className="mt-2 font-display text-2xl leading-tight text-[#f7f1ea]">
                  I test prototypes for real-world accuracy and precision.
                </p>
              </figcaption>
            </figure>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.1}>
            <div
              data-testid="tech-feature-instrument"
              className="relative flex h-full flex-col justify-between overflow-hidden rounded-sm border border-[var(--line)] bg-[#1c2b4a] p-8 md:p-10"
            >
              <CornerTicks />
              <span className="absolute right-4 top-4 rounded-sm bg-[#cbb890]/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#cbb890]">
                [ Fig. 02 — Instrument ]
              </span>
              <div className="flex flex-1 items-center justify-center py-4">
                <img
                  src="/tech-instrument.jpg"
                  alt="Calibryx General Surgery Forceps engraved with SHAW"
                  className="w-full max-w-xl rounded-sm object-contain shadow-2xl"
                />
              </div>
              <div className="mt-6">
                <span className="text-[11px] uppercase tracking-[0.26em] text-[#cbb890]">
                  Calibryx · General Surgery Forceps
                </span>
                <h3 className="mt-3 font-display text-3xl text-[#f7f1ea] md:text-4xl">
                  Ergonomic Design
                </h3>
                <p className="mt-3 max-w-lg text-base leading-relaxed text-[#cdd2dd]">
                  Ergonomic design to support physician hand fatigue.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Product lab catalogue */}
        <div className="mt-6 grid gap-px overflow-hidden rounded-sm border border-[var(--line)] bg-[var(--line)] md:grid-cols-2">
          {PROJECTS.items.map((p, i) => {
            const Card = p.href ? "a" : "div";
            const linkProps = p.href
              ? { href: p.href, target: "_blank", rel: "noopener noreferrer" }
              : {};
            return (
              <Reveal key={p.name} delay={i * 0.06}>
                <Card
                  {...linkProps}
                  data-testid={`project-${i}`}
                  className={`group relative flex h-full flex-col justify-between gap-10 bg-white p-8 transition-colors duration-500 hover:bg-[#faf8f5] md:p-12 ${
                    p.href ? "cursor-pointer" : ""
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <span className="font-mono text-xs text-[#1c2b4a]/40">
                      P-{String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#9c7a33]">
                      {p.tag}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display text-4xl text-[#1c2b4a] transition-colors duration-500 group-hover:text-[#9c7a33] md:text-5xl">
                      {p.name}
                    </h3>
                    <p className="mt-4 max-w-md text-base leading-relaxed text-[#5c6474]">
                      {p.body}
                    </p>
                    {p.href ? (
                      <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[#5c6474] transition-colors group-hover:text-[#9c7a33]">
                        Visit site <ArrowUpRight className="h-3.5 w-3.5" />
                      </span>
                    ) : null}
                  </div>
                  <span className="absolute inset-x-0 bottom-0 h-[2px] w-0 bg-[#9c7a33] transition-[width] duration-500 group-hover:w-full" />
                </Card>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
